import i18next from "../services/language/i18next";
import "core-js";

/**
 * Converts number into time string in format "00|01|02|...|59|etc."; adds 0 to number if necessary
 * @param {number} time - Time amount
 * @returns {string} - Time string in format "00|01|02|...|59|etc."
 */
export function to00(time) {
  const isTimeNotANumberType = typeof time !== "number" || Number.isNaN(time);
  const isIncorrectTime = (time < 0) || (time > 99);

  if (isTimeNotANumberType || isIncorrectTime) {
    return "";
  }

  const strTime = time.toString();
  return "0".repeat(2 - strTime.length) + strTime;
}

/**
 * Converts number into time string equivalent
 * @param {number} timeAmount - Amount of time
 * @param {string} [timeType=minute] - Type of time (second|minute|hour|day)
 * @returns {string} - Time in format "dd hh:mm:ss"
 */
export function numberToTimeDuration(timeAmount, timeType = "minute") {
  const secondsAmountForTimeTypes = {
    "second": 1,
    "minute": 60,
    "hour": 60 * 60,
    "day": 24 * 60 * 60,
  };

  const isTimeAmountNotANumberType = typeof timeAmount !== "number" || Number.isNaN(timeAmount);
  const isTimeTypeNotAStringType = typeof timeType !== "string";
  const isTimeAmountLessThanZero = timeAmount < 0;
  const secondsAmountForTimeType = secondsAmountForTimeTypes[timeType.toLowerCase?.()];

  if (isTimeAmountNotANumberType || isTimeTypeNotAStringType || isTimeAmountLessThanZero || !secondsAmountForTimeType) {
    return "";
  }

  let secondsAmount = timeAmount * secondsAmountForTimeType;

  const timeObj = Object.keys(secondsAmountForTimeTypes)
    .reverse()
    .reduce((timeObj, newTimeType, newTimeTypeIndex, timeTypesArray) => {
      timeObj[newTimeType] =
        (secondsAmount - timeTypesArray
            .slice(0, newTimeTypeIndex)
            .reduce((sum, calculatedTypeFactor) => sum + timeObj[calculatedTypeFactor] * secondsAmountForTimeTypes[calculatedTypeFactor], 0)
          - secondsAmount % secondsAmountForTimeTypes[newTimeType])
        / secondsAmountForTimeTypes[newTimeType];

      return timeObj;
    }, {});

  return `${timeObj.day ? `${timeObj.day} ` : ""}${to00(timeObj.hour)}:${to00(timeObj.minute)}:${to00(timeObj.second)}`;
}

/**
 * Convert string to seconds
 * @param {string} strValue - format "D HH:MM:SS"
 * @returns {number} - seconds
 */
export function convert_DHHMMSS_toSeconds(strValue) {
  if (typeof strValue !== "string") {
    throw new TypeError;
  }

  const resultArray = strValue.split(":")
    .map(item => {
      if (item.length > 2) return item.split(" ");
      return item;
    }).flat();
  let s = 0, m = 1;

  while (resultArray.length > 0) {
    s += m * parseInt(resultArray.pop(), 10);
    m *= m >= 3600 ? 24 : 60;
  }
  return s;
}

/**
 * Converts "dd hh:mm:ss" time string into minutes amount
 * @param {string} timeString - String of time in format "dd hh:mm:ss"
 * @returns {number} - Amount of minutes that are equivalent of time string
 */
export function convert_DHHMMSS_toMinutes(timeString) {
  const isTimeStringNotAStringType = typeof timeString !== "string";
  const DHHMMSSRegExp = /^(\d+ )?((\d{2}:){2})(\d{2})/gm;
  const isValidString = DHHMMSSRegExp.test(timeString);

  if (isTimeStringNotAStringType || !isValidString) {
    return -1;
  }

  return convert_DHHMMSS_toSeconds(timeString) / 60;
}

/**
 * Converts number into time string in the format "1 minutes|3 hours|3 days|etc."
 * @param {number} timeAmount -  Amount of time
 * @param {string} timeType - Type of time (second|minute|hour|day|etc.)
 * @returns {string} - Time in the format "1 minutes|3 hours|3 days|etc."
 */
export function getTranslatedTimeText(timeAmount, timeType) {
  const isTimeAmountNotANumberType = typeof timeAmount !== "number" || Number.isNaN(timeAmount);
  const isTimeTypeNotAStringType = typeof timeType !== "string";
  const isTimeAmountLessThanZero = timeAmount < 0;

  if (isTimeAmountNotANumberType || isTimeTypeNotAStringType || isTimeAmountLessThanZero) {
    return "";
  }

  return i18next.t(`time.${timeType}`, {count: timeAmount});
}

/**
 * Converts number of minutes into time string in the format "1 minutes|3 hours|3 days|etc."
 * @param {number} minutesAmount - Amount of minutes
 * @returns {string} - Time in the format "1 minutes|3 hours|3 days|etc."
 */
export function getTranslatedTimeTextFromMinutes(minutesAmount) {
  const isMinutesAmountNotANumberType = typeof minutesAmount !== "number" || Number.isNaN(minutesAmount);
  const isMinutesAmountLessThanZero = minutesAmount < 0;

  if (isMinutesAmountNotANumberType || isMinutesAmountLessThanZero) {
    return "";
  }

  let timeText;

  if (minutesAmount < 60) {
    timeText = getTranslatedTimeText(minutesAmount, "minute");
  } else if (minutesAmount < 1440) {
    const hoursAmount = +(minutesAmount / 60).toFixed(2);
    timeText = getTranslatedTimeText(hoursAmount, "hour");
  } else {
    const daysAmount = +(minutesAmount / 60 / 24).toFixed(2);
    timeText = getTranslatedTimeText(daysAmount, "day");
  }

  return timeText;
}

/**
 * Converts rule notification times from the format "01:02:00" to the format "01:02"
 * @param {object} ruleData - Rule data object
 * @returns {object} - Same rule data object but with sliced "from_time" and "to_time" fields
 */
export function sliceRuleNotificationTimes(ruleData) {
  ruleData.from_time = ruleData.from_time.slice(0, 5) || null;
  ruleData.to_time = ruleData.to_time.slice(0, 5) || null;
  return ruleData;
}